import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Video, Map, User, MessageCircle, Plus } from 'lucide-react';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

/**
 * BottomNavigation - нижняя навигация для мобильных устройств
 * Отображается только на мобильных устройствах
 * Безопасно: не влияет на десктоп версию
 */
const BottomNavigation = () => {
  const device = useDeviceDetection();
  const location = useLocation();
  const { t } = useTranslation();
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  
  // Определяем, открыта ли клавиатура
  useEffect(() => {
    if (!device.isMobile) return;
    
    // В landscape режиме всегда показываем панель (не скрываем)
    if (device.isLandscape) {
      setIsKeyboardVisible(false);
      return;
    }
    
    // Храним предыдущую высоту viewport для сравнения (только для portrait)
    let lastViewportHeight = device.screenHeight;
    let hasActiveInput = false;
    
    const handleResize = () => {
      // Используем device.screenHeight вместо visualViewport (более надежно)
      const currentHeight = device.screenHeight;
      
      // Если высота viewport уменьшилась более чем на 150px И есть активный input, вероятно открыта клавиатура
      const heightDiff = lastViewportHeight - currentHeight;
      if (heightDiff > 150 && hasActiveInput) {
        setIsKeyboardVisible(true);
      } else if (heightDiff < -50) {
        // Если высота увеличилась (клавиатура закрылась)
        setIsKeyboardVisible(false);
        lastViewportHeight = currentHeight;
      } else {
        // Обновляем lastViewportHeight для следующего сравнения
        lastViewportHeight = currentHeight;
      }
    };
    
    // Обновляем при изменении размеров
    window.addEventListener('resize', handleResize);
    
    // Также слушаем фокус на input/textarea
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        hasActiveInput = true;
        // Обновляем lastViewportHeight при фокусе
        lastViewportHeight = device.screenHeight;
        // Небольшая задержка, чтобы клавиатура успела открыться
        setTimeout(() => {
          const currentHeight = device.screenHeight;
          const heightDiff = lastViewportHeight - currentHeight;
          if (heightDiff > 150) {
            setIsKeyboardVisible(true);
          }
        }, 300);
      }
    };
    
    const handleBlur = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        hasActiveInput = false;
        // Небольшая задержка, чтобы клавиатура успела закрыться
        setTimeout(() => {
          const currentHeight = device.screenHeight;
          const heightDiff = lastViewportHeight - currentHeight;
          if (heightDiff < 150) {
            setIsKeyboardVisible(false);
            lastViewportHeight = currentHeight;
          }
        }, 300);
      }
    };
    
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, [device.isMobile, device.isLandscape, device.screenHeight]);
  
  // Показываем только на мобильных устройствах
  if (!device.isMobile) return null;
  
  const handleMessengerClick = (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('openMessenger', { detail: { chat: null } }));
  };

  const handleCreateRouteClick = (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('map:openCreateRoute'));
  };

  const baseNavItems = [
    { path: '/feed', icon: Video, label: t('navigation.feed', 'Лента'), translationKey: 'navigation.feed', isLink: true },
    { path: '/map', icon: Map, label: t('navigation.map', 'Карта'), translationKey: 'navigation.map', isLink: true },
  ];
  const createRouteItem = {
    path: '/map-create',
    icon: Plus,
    label: t('map.createRoute', 'Создать маршрут'),
    translationKey: 'map.createRoute',
    isLink: false,
    isCreateButton: true,
    onClick: handleCreateRouteClick,
  };
  const tailNavItems = [
    { path: '/profile', icon: User, label: t('navigation.profile', 'Профиль'), translationKey: 'navigation.profile', isLink: true },
    { path: '/messenger', icon: MessageCircle, label: t('navigation.messenger', 'Чаты'), translationKey: 'navigation.messenger', isLink: false, onClick: handleMessengerClick },
  ];
  const navItems = location.pathname === '/map'
    ? [...baseNavItems, createRouteItem, ...tailNavItems]
    : [...baseNavItems, ...tailNavItems];
  
  return (
<nav
      className={`app-bottom-nav fixed left-0 right-0 border-t z-40 transition-transform duration-300 ${
        isKeyboardVisible ? 'translate-y-full' : 'bottom-0'
      }`}
      style={{
        backgroundColor: 'var(--footer-bg)',
        borderColor: 'var(--footer-border)',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        boxSizing: 'border-box',
        minHeight: '44px',
        maxHeight: '44px',
        padding: 0,
        paddingBottom: 'env(safe-area-inset-bottom, 0)'
      }}
    >
      <div className="flex justify-around items-center" style={{ height: '44px' }}>
        {navItems.map(item => {
          const Icon = item.icon;
          const isActive = item.isLink && (location.pathname === item.path || 
                          (item.path === '/profile' && location.pathname === '/'));
          
          const Component = item.isLink ? Link : 'button';
          const props = item.isLink 
            ? { 
                to: item.path,
                onMouseEnter: item.path === '/feed' ? () => {
                  // Preload FeedPage chunk при наведении
                  import('../../pages/FeedPage/FeedPage');
                } : undefined
              }
            : { onClick: item.onClick, type: 'button' };
          
          const isCreateButton = item.isCreateButton === true;
          return (
            <Component
              key={item.path}
              {...props}
              className={`
                flex flex-col items-center justify-center flex-1 relative
                transition-colors duration-200
                min-h-[40px] min-w-[44px]
                ${isCreateButton
                  ? 'text-white'
                  : isActive
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }
              `}
              aria-label={item.label}
            >
              {isCreateButton ? (
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 shadow-md -mt-5 transition-colors">
                  <Icon className="h-5 w-5" />
                </span>
              ) : (
                <>
                  <Icon className="h-5 w-5 mb-0.5" />
                  <span className="text-[10px] font-medium leading-tight">{item.label}</span>
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-t-full" />
                  )}
                </>
              )}
            </Component>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;

